/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import type { Klass, LexicalNode } from 'lexical';
import 'prismjs';
import 'prismjs/components/prism-javascript';
import { CodeHighlightNode, CodeNode } from '@lexical/code';
import { HashtagNode } from '@lexical/hashtag';
import { AutoLinkNode, LinkNode } from '@lexical/link';
import { ListItemNode, ListNode } from '@lexical/list';
import { MarkNode } from '@lexical/mark';
import { OverflowNode } from '@lexical/overflow';
import { HorizontalRuleNode } from '@lexical/react/LexicalHorizontalRuleNode';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { TableCellNode, TableNode, TableRowNode } from '@lexical/table';

import { CollapsibleContainerNode } from '../plugins/CollapsiblePlugin/CollapsibleContainerNode';
import { CollapsibleContentNode } from '../plugins/CollapsiblePlugin/CollapsibleContentNode';
import { CollapsibleTitleNode } from '../plugins/CollapsiblePlugin/CollapsibleTitleNode';
import { AutocompleteNode } from './AutocompleteNode';
import { EmojiNode } from './EmojiNode';
import { ExcalidrawNode } from './ExcalidrawNode';
import { FigmaNode } from './FigmaNode';
import { ImageNode } from './ImageNode';
import { InlineImageNode } from './InlineImageNode/InlineImageNode';
import { KeywordNode } from './KeywordNode';
import { LayoutContainerNode } from './LayoutContainerNode';
import { LayoutItemNode } from './LayoutItemNode';
import { MentionNode } from './MentionNode';
import { PageBreakNode } from './PageBreakNode';
import { PollNode } from './PollNode';
import { StickyNode } from './StickyNode';
import { TweetNode } from './TweetNode';
import { YouTubeNode } from './YouTubeNode';
import { TabContainerNode } from './TabContainerNode';
import { TabItemNode } from './TabItemNode';
import { FirmwareTableNode } from './FirmwareTableNode.tsx';
import { LocalizableTextNode } from 'pages/authorized/page-designer/lexical/nodes/Localizable/LocalizableTextNode.tsx';
import { ProtectedParagraphNode } from './Localizable/ProtectedParagraphNode.ts';
import { LocalizationNode } from './Localizable/LocalizationNode.tsx';
import { LocalizationLinkNode } from './Localizable/LocalizationLinkNode.ts';

const PlaygroundNodes: Array<Klass<LexicalNode>> = [
  HeadingNode,
  ListNode,
  ListItemNode,
  QuoteNode,
  CodeNode,
  TableNode,
  TableCellNode,
  TableRowNode,
  HashtagNode,
  CodeHighlightNode,
  AutoLinkNode,
  LinkNode,
  OverflowNode,
  PollNode,
  StickyNode,
  ImageNode,
  InlineImageNode,
  MentionNode,
  EmojiNode,
  ExcalidrawNode,
  AutocompleteNode,
  KeywordNode,
  HorizontalRuleNode,
  TweetNode,
  YouTubeNode,
  FigmaNode,
  MarkNode,
  CollapsibleContainerNode,
  CollapsibleContentNode,
  CollapsibleTitleNode,
  PageBreakNode,
  LayoutContainerNode,
  LayoutItemNode,
  TabContainerNode,
  TabItemNode,
  FirmwareTableNode,
  LocalizableTextNode,
  ProtectedParagraphNode,
  LocalizationNode,
  LocalizationLinkNode,
];

export default PlaygroundNodes;
